import type { Signal } from "@preact/signals-react";
import { batch, signal } from "@preact/signals-react";
import { useSignals } from "@preact/signals-react/runtime";
import { usePathname } from "next/navigation";
import type { ReactElement, ReactNode } from "react";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
} from "react";

import type { EmbedURLMetadata } from "@/components/app/post/embed-player";
import { feedFreezeDebugLog } from "@/utils/feed-freeze-debug";
import { DEFAULT_IMAGE_DIMENSIONS } from "@/utils/media-dimensions";
import {
  getMarkdownBody,
  MIN_CHAR_TO_SHOW,
  processRawPostContentToHTMLContent,
  smartPostBodyCut,
  stripPostBodyFooter,
} from "@/utils/post-utils";

type PostCacheContextType = {
  schedulePostBodyCacheWarmup: (
    options: SchedulePostBodyCacheWarmupOptions,
  ) => () => void;
  postBodyMDCache: Map<string, MarkdownCacheRecord>;
  getPostImageSize: (url: string) => {
    width: number;
    height: number;
    isFallback?: boolean;
  };
  /**
   * @deprecated Please use the metadata coming from the backend instead.
   * If there's no metadata, make sure there is :)
   */
  postsImageSizeMap: Map<
    string,
    {
      width: number;
      height: number;
    }
  >;
  decryptedGatedContentCache: Map<string, DecryptedGatedContentCacheEntry>;
  getCacheSignal: (postHash: string) => PostCacheSignal;
  loadIntersectionObserver: IntersectionObserver;
  viewIntersectionObserver: IntersectionObserver;
  resizeObserver: ResizeObserver;
  signalMap: Map<string, PostCacheSignal>;
  urlDetectionCache: Map<string, EmbedURLMetadata[]>;
};

export type DecryptedGatedContentCacheEntry = {
  postBody: string;
  imageURLs: string[];
  videoURLs: string[];
  audioURLs: string[];
  pollData: {
    PollWeightType: string;
    PollOptions: string;
    PollWeightTokenPublicKey: string;
  };
  isSubscribed: boolean;
};

const PostCacheContext = createContext<PostCacheContextType>(
  undefined as never,
);

type MarkdownCacheRecord = {
  body: ReactElement<unknown> | string;
  shortBody?: ReactElement<unknown> | null | string;
};

type PostBodyWarmupPriority = "idle" | "visible";

type SchedulePostBodyCacheWarmupOptions = {
  cacheKey: string;
  body: string;
  isMarkdown?: boolean;
  priority?: PostBodyWarmupPriority;
  onComplete?: (record: MarkdownCacheRecord) => void;
};

type PostBodyWarmupTask = {
  cacheKey: string;
  body: string;
  isMarkdown: boolean;
  priority: PostBodyWarmupPriority;
  listeners: Set<(record: MarkdownCacheRecord) => void>;
};

type PostCacheSignalEntry = {
  loadIntersecting?: boolean;
  viewIntersecting?: boolean;
  wrapperWidth?: number;
  wrapperHeight?: number;
  bodyWidth?: number;
  bodyHeight?: number;
  viewTracked?: boolean;
  revealed?: boolean;
  nsfwRevealed?: boolean;
  pollHeight?: number;
  wrapperWindowWidth?: number;
  bodyWindowWidth?: number;
  embedsHeight?: Record<string, number>;
  embedsWidth?: Record<string, number>;
  trackings: Set<string>;
  fixed?: boolean;
  forceUpdate: () => void;
  showCommentEditor: boolean;
};

type PostCacheSignal = Signal<PostCacheSignalEntry>;

let loadIntersectionObserver: IntersectionObserver;
let viewIntersectionObserver: IntersectionObserver;
let resizeObserver: ResizeObserver;

const signalMap = new Map<string, PostCacheSignal>();
const POST_BODY_WARMUP_BATCH_BUDGET_MS = 8;

const getCacheSignal = (cacheKey: string) => {
  let cacheSignal = signalMap.get(cacheKey) as PostCacheSignal;
  if (!cacheSignal) {
    cacheSignal = signal({
      loadIntersecting: undefined,
      viewIntersecting: undefined,
      viewTracked: undefined,
      wrapperWidth: undefined,
      wrapperHeight: undefined,
      bodyWidth: undefined,
      bodyHeight: undefined,
      revealed: undefined,
      pollHeight: undefined,
      windowWidth: undefined,
      nsfwRevealed: undefined,
      showCommentEditor: false,
      embedsHeight: {},
      embedsWidth: {},
      cacheKey: cacheKey,
      trackings: new Set(),
      fixed: undefined,
      forceUpdate() {
        feedFreezeDebugLog(
          "post-cache",
          "forceUpdate:requested",
          {
            cacheKey,
            tracks: Array.from(cacheSignal.value.trackings),
          },
          {
            throttleKey: `post-cache-force-update:${cacheKey}`,
            throttleMs: 500,
          },
        );

        // We do it with some time to make sure
        // rendering has been finished.
        setTimeout(() => {
          for (const track of cacheSignal.value.trackings) {
            const widthCacheKey = `${track}Width` as const;
            const heightCacheKey = `${track}Height` as const;
            const elements = document.querySelectorAll(
              `[data-cache-key="${cacheKey}"][data-track="${track}"]`,
            );
            if (!elements || !cacheSignal) {
              return;
            }

            elements.forEach((element) => {
              const trackId = element.getAttribute("data-track-id");
              const height = Math.ceil(element.getBoundingClientRect().height);
              const width = Math.ceil(element.getBoundingClientRect().width);

              feedFreezeDebugLog(
                "post-cache",
                "forceUpdate:measure",
                {
                  cacheKey,
                  height,
                  track,
                  trackId,
                  width,
                },
                {
                  throttleKey: `post-cache-force-measure:${cacheKey}:${track}:${trackId ?? "root"}`,
                  throttleMs: 500,
                },
              );

              if (trackId) {
                cacheSignal.value = {
                  ...cacheSignal.value,
                  [heightCacheKey]: {
                    // @ts-expect-error hard to type this correctly
                    ...cacheSignal.value[heightCacheKey],
                    [trackId]: height,
                  },
                  [widthCacheKey]: {
                    // @ts-expect-error hard to type this correctly
                    ...cacheSignal.value[widthCacheKey],
                    [trackId]: width,
                  },
                };
              } else {
                cacheSignal.value = {
                  ...cacheSignal.value,
                  [heightCacheKey]: height,
                  [widthCacheKey]: width,
                };
              }
            });
          }
        }, 10);
      },
    });
    signalMap.set(cacheKey, cacheSignal);
  }
  return cacheSignal;
};

export const PostCacheProvider = ({ children }: { children: ReactNode }) => {
  const postBodyMDCacheRef = useRef<PostCacheContextType["postBodyMDCache"]>(
    new Map(),
  );
  const postBodyWarmupQueueRef = useRef(new Map<string, PostBodyWarmupTask>());
  const postBodyWarmupVisibleHandleRef = useRef<number | null>(null);
  const postBodyWarmupIdleHandleRef = useRef<number | null>(null);
  const decryptedGatedContentCacheRef = useRef<
    PostCacheContextType["decryptedGatedContentCache"]
  >(new Map());
  const postsImageSizeMapRef = useRef<
    PostCacheContextType["postsImageSizeMap"]
  >(new Map());
  const urlDetectionCacheRef = useRef(new Map());

  if (typeof window !== "undefined" && !loadIntersectionObserver) {
    loadIntersectionObserver = new IntersectionObserver(
      (entries) => {
        batch(() => {
          for (const entry of entries) {
            const cacheKey = entry.target.getAttribute("data-cache-key");
            if (!cacheKey) {
              console.error(
                "data-cache-key attribute is required on the observed element",
                entry.target,
              );
              return;
            }

            const cacheSignal: PostCacheSignal = getCacheSignal(cacheKey);

            if (
              cacheSignal &&
              cacheSignal.value.loadIntersecting !== entry.isIntersecting
            ) {
              feedFreezeDebugLog(
                "post-cache",
                "loadIntersection:update",
                {
                  cacheKey,
                  isIntersecting: entry.isIntersecting,
                },
                {
                  throttleKey: `post-cache-load-io:${cacheKey}`,
                  throttleMs: 250,
                },
              );

              cacheSignal.value = {
                ...cacheSignal.value,
                loadIntersecting: entry.isIntersecting,
              };
            }
          }
        });
      },
      { threshold: 0, rootMargin: "2000px", root: document },
    );

    viewIntersectionObserver = new IntersectionObserver(
      (entries) => {
        batch(() => {
          for (const entry of entries) {
            const cacheKey = entry.target.getAttribute("data-cache-key");
            if (!cacheKey) {
              console.error(
                "data-cache-key attribute is required on the observed element",
                entry.target,
              );
              return;
            }

            const cacheSignal: PostCacheSignal = getCacheSignal(cacheKey);

            if (
              cacheSignal &&
              cacheSignal.value.viewIntersecting !== entry.isIntersecting
            ) {
              feedFreezeDebugLog(
                "post-cache",
                "viewIntersection:update",
                {
                  cacheKey,
                  isIntersecting: entry.isIntersecting,
                },
                {
                  throttleKey: `post-cache-view-io:${cacheKey}`,
                  throttleMs: 250,
                },
              );

              cacheSignal.value = {
                ...cacheSignal.value,
                viewIntersecting: entry.isIntersecting,
              };
            }
          }
        });
      },
      { threshold: [0.03], rootMargin: "0px 0px 200px 0px", root: document },
    );

    resizeObserver = new ResizeObserver((entries: ResizeObserverEntry[]) => {
      const windowWidth = window.innerWidth;

      requestAnimationFrame(() => {
        batch(() => {
          for (const entry of entries) {
            const cacheKey = entry.target.getAttribute("data-cache-key");
            const track = entry.target.getAttribute("data-track");
            const trackId = entry.target.getAttribute("data-track-id");

            if (!cacheKey || !track) {
              console.error(
                "data-cache-key and data-track attributes are required on the observed element",
                entry.target,
              );
              return;
            }

            const cacheSignal = getCacheSignal(cacheKey);
            cacheSignal.value.trackings.add(track);

            if (cacheSignal.value.loadIntersecting) {
              const height = Math.ceil(entry.contentRect.height);
              const width = Math.ceil(entry.contentRect.width);
              const heightCacheKey = `${track}Height` as const;
              const widthCacheKey = `${track}Width` as const;
              const windowWidthCacheKey = `${track}WindowWidth` as const;
              const currentHeight = trackId
                ? // @ts-expect-error hard to type this correctly
                  cacheSignal.value[heightCacheKey][trackId]
                : // @ts-expect-error hard to type this correctly
                  cacheSignal.value[heightCacheKey];
              // @ts-expect-error hard to type this correctly
              const currentWindowWidth = cacheSignal.value[windowWidthCacheKey];

              // We update if the window width has changed
              // or if the new height is greater than the current height.
              if (
                currentWindowWidth !== windowWidth ||
                (currentHeight ?? 0) < height
              ) {
                feedFreezeDebugLog(
                  "post-cache",
                  "resize:update",
                  {
                    cacheKey,
                    currentHeight: currentHeight ?? null,
                    currentWindowWidth: currentWindowWidth ?? null,
                    height,
                    track,
                    trackId,
                    width,
                    windowWidth,
                  },
                  {
                    throttleKey: `post-cache-resize:${cacheKey}:${track}:${trackId ?? "root"}`,
                    throttleMs: 250,
                  },
                );

                const newDimensions: Partial<PostCacheSignalEntry> = trackId
                  ? {
                      [heightCacheKey]: {
                        // @ts-expect-error hard to type this correctly
                        ...cacheSignal.value[heightCacheKey],
                        [trackId]: height,
                      },
                      [widthCacheKey]: {
                        // @ts-expect-error hard to type this correctly
                        ...cacheSignal.value[widthCacheKey],
                        [trackId]: width,
                      },
                      [windowWidthCacheKey]: windowWidth,
                    }
                  : {
                      [heightCacheKey]: height,
                      [widthCacheKey]: width,
                      [windowWidthCacheKey]: windowWidth,
                    };

                cacheSignal.value = {
                  ...cacheSignal.value,
                  ...newDimensions,
                };
              }
            }
          }
        });
      });
    });
  }

  const createPostBodyCacheRecord = useCallback(
    (body: string, isMarkdown = true) => {
      const cleanBody = stripPostBodyFooter(body);
      const hasShortBody = cleanBody && cleanBody.length > MIN_CHAR_TO_SHOW;

      return {
        body: isMarkdown
          ? getMarkdownBody(cleanBody)
          : processRawPostContentToHTMLContent(cleanBody),
        shortBody: hasShortBody
          ? isMarkdown
            ? getMarkdownBody(smartPostBodyCut(cleanBody, MIN_CHAR_TO_SHOW))
            : processRawPostContentToHTMLContent(
                smartPostBodyCut(cleanBody, MIN_CHAR_TO_SHOW),
              )
          : null,
      };
    },
    [],
  );

  const flushPostBodyWarmups = useCallback(
    (deadline?: IdleDeadline) => {
      const startedAt = performance.now();
      const queue = postBodyWarmupQueueRef.current;
      const postBodyMDCache = postBodyMDCacheRef.current;
      const tasks = Array.from(queue.values()).sort((left, right) => {
        if (left.priority === right.priority) {
          return 0;
        }

        return left.priority === "visible" ? -1 : 1;
      });

      const shouldYield = () => {
        if (deadline) {
          return deadline.timeRemaining() < 1;
        }

        return (
          performance.now() - startedAt >= POST_BODY_WARMUP_BATCH_BUDGET_MS
        );
      };

      for (const task of tasks) {
        if (shouldYield()) {
          break;
        }

        const existingRecord = postBodyMDCache.get(task.cacheKey);
        if (existingRecord) {
          task.listeners.forEach((listener) => listener(existingRecord));
          queue.delete(task.cacheKey);
          continue;
        }

        const record = createPostBodyCacheRecord(task.body, task.isMarkdown);
        postBodyMDCache.set(task.cacheKey, record);
        task.listeners.forEach((listener) => listener(record));
        queue.delete(task.cacheKey);
      }
    },
    [createPostBodyCacheRecord],
  );

  const schedulePostBodyWarmupFlush = useCallback(
    (priority: PostBodyWarmupPriority) => {
      if (typeof window === "undefined") {
        return;
      }

      if (priority === "visible") {
        if (postBodyWarmupVisibleHandleRef.current !== null) {
          return;
        }

        postBodyWarmupVisibleHandleRef.current = window.setTimeout(() => {
          postBodyWarmupVisibleHandleRef.current = null;
          flushPostBodyWarmups();

          if (postBodyWarmupQueueRef.current.size > 0) {
            schedulePostBodyWarmupFlush("visible");
          }
        }, 0);

        return;
      }

      if (
        postBodyWarmupIdleHandleRef.current !== null ||
        postBodyWarmupVisibleHandleRef.current !== null
      ) {
        return;
      }

      if (typeof globalThis.requestIdleCallback === "function") {
        postBodyWarmupIdleHandleRef.current = globalThis.requestIdleCallback(
          (deadline) => {
            postBodyWarmupIdleHandleRef.current = null;
            flushPostBodyWarmups(deadline);

            if (postBodyWarmupQueueRef.current.size > 0) {
              schedulePostBodyWarmupFlush("idle");
            }
          },
          { timeout: 300 },
        );
        return;
      }

      postBodyWarmupIdleHandleRef.current = window.setTimeout(() => {
        postBodyWarmupIdleHandleRef.current = null;
        flushPostBodyWarmups();

        if (postBodyWarmupQueueRef.current.size > 0) {
          schedulePostBodyWarmupFlush("idle");
        }
      }, 16);
    },
    [flushPostBodyWarmups],
  );

  const schedulePostBodyCacheWarmup = useCallback(
    ({
      cacheKey,
      body,
      isMarkdown = true,
      priority = "idle",
      onComplete,
    }: SchedulePostBodyCacheWarmupOptions) => {
      const postBodyMDCache = postBodyMDCacheRef.current;
      const existingRecord = postBodyMDCache.get(cacheKey);

      if (existingRecord) {
        onComplete?.(existingRecord);
        return () => {};
      }

      if (!body) {
        return () => {};
      }

      let task = postBodyWarmupQueueRef.current.get(cacheKey);

      if (!task) {
        task = {
          cacheKey,
          body,
          isMarkdown,
          priority,
          listeners: new Set(),
        };
        postBodyWarmupQueueRef.current.set(cacheKey, task);
      } else if (priority === "visible") {
        task.priority = "visible";
      }

      if (onComplete) {
        task.listeners.add(onComplete);
      }

      schedulePostBodyWarmupFlush(task.priority);

      return () => {
        if (!onComplete) {
          return;
        }

        task?.listeners.delete(onComplete);
      };
    },
    [schedulePostBodyWarmupFlush],
  );

  useEffect(() => {
    return () => {
      if (postBodyWarmupVisibleHandleRef.current !== null) {
        window.clearTimeout(postBodyWarmupVisibleHandleRef.current);
      }

      if (postBodyWarmupIdleHandleRef.current !== null) {
        if (typeof globalThis.cancelIdleCallback === "function") {
          globalThis.cancelIdleCallback(postBodyWarmupIdleHandleRef.current);
        } else {
          window.clearTimeout(postBodyWarmupIdleHandleRef.current);
        }
      }
    };
  }, []);

  const getPostImageSize = useCallback((url: string) => {
    return (
      postsImageSizeMapRef?.current?.get(url) ?? {
        ...DEFAULT_IMAGE_DIMENSIONS,
        isFallback: true,
      }
    );
  }, []);

  return (
    <PostCacheContext.Provider
      value={{
        getPostImageSize,
        schedulePostBodyCacheWarmup,
        postBodyMDCache: postBodyMDCacheRef.current,
        postsImageSizeMap: postsImageSizeMapRef.current,
        decryptedGatedContentCache: decryptedGatedContentCacheRef.current,
        getCacheSignal,
        signalMap,
        loadIntersectionObserver,
        viewIntersectionObserver,
        resizeObserver,
        urlDetectionCache: urlDetectionCacheRef.current,
      }}
    >
      {children}
    </PostCacheContext.Provider>
  );
};

type ExtendedPostCacheContextType = PostCacheContextType &
  PostCacheSignalEntry & {
    postCacheSignal: PostCacheSignal;
    postCacheKey: string;
  };

export const useOptionalPostCacheContext = () => {
  return useContext(PostCacheContext) as PostCacheContextType | undefined;
};

export function usePostCacheContext(): PostCacheContextType;
export function usePostCacheContext(
  postHash: string,
): ExtendedPostCacheContextType;
export function usePostCacheContext(
  postHash: string,
  cacheKeyPrefix: string | boolean | undefined,
): ExtendedPostCacheContextType;
export function usePostCacheContext(
  postHash?: string,
  cacheKeyPrefix?: string | boolean | undefined,
) {
  const context = useOptionalPostCacheContext();

  if (context === undefined) {
    throw new Error("usage outside of PostCacheContext");
  }

  if (postHash || (postHash && !cacheKeyPrefix)) {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    useSignals();
    let extendedContext = context as ExtendedPostCacheContextType;

    if (cacheKeyPrefix === true) {
      // eslint-disable-next-line react-hooks/rules-of-hooks
      cacheKeyPrefix = usePathname();
    }

    const postCacheKey = cacheKeyPrefix
      ? `${cacheKeyPrefix}-${postHash}`
      : postHash;

    if (typeof window !== "undefined") {
      const postCacheSignal = context.getCacheSignal(postCacheKey);

      extendedContext = {
        ...extendedContext,
        postCacheSignal,
        ...postCacheSignal.value,
      };
    }

    return {
      ...extendedContext,
      postCacheKey,
    };
  }

  return context;
}
